import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowLeft, Home } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Stagger, StaggerItem } from "@/components/motion";

const QUICK_LINKS = [
  { to: "/", label: "Home", desc: "Which Media at a glance" },
  { to: "/brands", label: "Our Brands", desc: "The six brands under Which Media" },
  { to: "/news", label: "News & Press", desc: "Latest announcements & research" },
  { to: "/careers", label: "Careers", desc: "Join the team" },
  { to: "/contact", label: "Contact", desc: "Get in touch" },
];

const NotFound = () => {
  const { pathname } = useLocation();
  return (
    <div data-testid="not-found-page" className="min-h-[100vh] pt-[76px] bg-ink text-white">
      <Seo
        title="Page not found"
        path={pathname}
        description="The page you're looking for isn't at this address on the Which Media website."
        noindex
      />
      <section className="mx-auto max-w-[1400px] px-5 md:px-8 pt-16 md:pt-28 pb-20">
        <FadeUp>
          <p className="font-mono text-xs font-extrabold uppercase tracking-[0.2em] text-primary">
            Error 404 · Off-map
          </p>
          <h1
            className="headline-xl uppercase text-[22vw] md:text-[16rem] leading-[0.85] text-white/95 mt-8"
            data-testid="not-found-heading"
          >
            Lost.
          </h1>
          <p className="mt-8 max-w-2xl text-xl md:text-2xl font-medium leading-snug text-white/70">
            The page{" "}
            <code className="font-mono text-primary bg-white/5 px-2 py-1 rounded-sm break-all">{pathname}</code>{" "}
            isn&rsquo;t part of the Which Media map — either it moved, or it was never here.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/"
              data-testid="notfound-home"
              className="inline-flex items-center gap-2 font-sans text-sm font-bold bg-primary text-primary-foreground rounded-full pl-6 pr-5 py-3.5 hover:bg-white hover:text-ink transition-colors"
            >
              <Home size={16} /> Go home <ArrowRight size={16} />
            </Link>
            <button
              type="button"
              onClick={() => window.history.back()}
              data-testid="notfound-back"
              className="inline-flex items-center gap-2 font-sans text-sm font-bold border-2 border-white/25 text-white/80 rounded-full pl-5 pr-6 py-3 hover:border-white hover:text-white transition-colors"
            >
              <ArrowLeft size={16} /> Go back
            </button>
          </div>
        </FadeUp>

        <div className="mt-24 border-t border-white/10 pt-12">
          <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-8">
            Try one of these instead
          </p>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {QUICK_LINKS.map((l) => (
              <StaggerItem key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`notfound-link-${l.to.replace(/\//g, "") || "home"}`}
                  className="group block border border-white/15 hover:border-primary rounded-xl p-5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="headline text-xl md:text-2xl uppercase text-white group-hover:text-primary transition-colors leading-tight">
                        {l.label}
                      </p>
                      <p className="mt-1.5 text-sm text-white/60 leading-snug">{l.desc}</p>
                    </div>
                    <ArrowRight
                      size={20}
                      className="mt-1 shrink-0 text-white/40 group-hover:text-primary group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
